/**
 * SuppliersDashboard Component
 * 
 * Dashboard for managing supplier data using the generic DataDashboard system.
 * Currently displays placeholder content until backend integration is complete.
 * 
 * @component
 * @example
 * <SuppliersDashboard
 *   user={adminUser}
 *   currentRoute="/dashboard/suppliers"
 *   onNavigate={handleNavigate}
 *   onLogout={handleLogout}
 * />
 */

import React, { useState, useEffect, useMemo } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import AddSupplierModal from '../../modals/AddSupplierModal';
import RegionSelector, { Region } from '../../../common/RegionSelector';
import CompanySelector from '../../../common/CompanySelector';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import Toast from '../../../../components/Toast';
import { getCategoryName } from '../../../../utils/categories';

interface SuppliersDashboardProps {
  /** Current admin user */
  user: AdminUser;
  /** Current route */
  currentRoute: string;
  /** Navigation handler */
  onNavigate: (route: string) => void;
  /** Logout handler */
  onLogout: () => void;
}

const SuppliersDashboard: React.FC<SuppliersDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const [region, setRegion] = useState<Region>('tr'); // Default to Turkey
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Company filter states
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Add Supplier Modal states
  const [showAddModal, setShowAddModal] = useState(false);

  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [suppliersToDelete, setSuppliersToDelete] = useState<string[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // Store current filter/search/sort state for reloading after delete
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [currentSortBy, setCurrentSortBy] = useState<string>('global_id');
  const [currentSortOrder, setCurrentSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentColumnFilters, setCurrentColumnFilters] = useState<Record<string, string>>({});

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle add supplier
  const handleAddSupplier = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleSupplierAdded = () => {
    // Refresh the suppliers list
    handleDataFetch({
      page: paginationInfo.page,
      limit: paginationInfo.limit,
      search: '',
      sortBy: 'name',
      sortOrder: 'asc',
      columnFilters: {}
    });
    setShowAddModal(false);
  };

  // Base supplier columns definition (without recommended_by_companies)
  const baseSupplierColumns = [
    { key: 'global_id', title: 'Global ID', type: 'text' as const, sortable: true, editable: false, width: '100px' },
    { key: 'name', title: 'Name', type: 'text' as const, sortable: true, editable: true, width: '200px' },
    {
      key: 'category',
      title: 'Category',
      type: 'categories' as const,
      sortable: true,
      editable: true,
      width: '150px',
      render: (value: any) => {
        if (Array.isArray(value)) {
          return value.map(v => getCategoryName(String(v))).join(', ');
        }
        return getCategoryName(String(value)) || value;
      }
    },
    { key: 'address', title: 'Address', type: 'text' as const, sortable: false, editable: true, width: '250px' },
    { key: 'website', title: 'Website', type: 'text' as const, sortable: false, editable: true, width: '150px' },
    { key: 'online_sales', title: 'Online Sales', type: 'status' as const, sortable: true, editable: true, width: '120px' },
    { key: 'online_marketing', title: 'Online Marketing', type: 'status' as const, sortable: true, editable: true, width: '140px' },
    { key: 'approval_status', title: 'Approval', type: 'status' as const, sortable: true, editable: true, width: '120px' },
    { key: 'overall_score', title: 'Overall Score', type: 'score' as const, sortable: true, editable: false, width: '120px' },
    { key: 'trustpilot_score', title: 'Trustpilot', type: 'score' as const, sortable: true, editable: true, width: '100px' },
    { key: 'google_maps_score', title: 'Google Maps', type: 'score' as const, sortable: true, editable: true, width: '100px' },
    { key: 'mebsis_score', title: 'Mebsis', type: 'score' as const, sortable: true, editable: true, width: '80px' },
    { key: 'email_score', title: 'Email Score', type: 'score' as const, sortable: true, editable: true, width: '100px' },
    { key: 'company_specific_codes', title: 'Company Codes', type: 'companies' as const, sortable: false, editable: true, width: '200px' },
    { key: 'status', title: 'Status', type: 'status' as const, sortable: true, editable: true, width: '100px' }
  ];

  // Dynamic columns based on selected companies
  const supplierColumns: any[] = useMemo(() => {
    const cols: any[] = [...baseSupplierColumns];

    const companyCodesIndex = cols.findIndex(col => col.key === 'company_specific_codes');

    if (selectedCompanies.length > 0) {
      // Remove the generic company_specific_codes column
      if (companyCodesIndex !== -1) {
        cols.splice(companyCodesIndex, 1);
      }

      // Add individual columns for each selected company
      selectedCompanies.forEach((company, index) => {
        cols.splice(companyCodesIndex !== -1 ? companyCodesIndex + index : cols.length - 1, 0, {
          key: `company_code_${company.company_id}`,
          title: `${company.company_name} Code`,
          type: 'text' as const,
          sortable: false,
          editable: true,
          width: '150px',
          render: (value: any, row: any) => {
            const companyCodes = row.company_specific_codes || [];
            const companyCode = companyCodes.find((c: any) => {
              return c.company_id === company.company_id;
            });
            return companyCode?.code || '';
          }
        });
      });
    }

    return cols;
  }, [selectedCompanies, baseSupplierColumns]);

  // Real data loading function using V2 API with region support
  const loadSuppliers = async (
    page: number = 1,
    limit: number = 25,
    search: string = '',
    sortBy: string = 'global_id',
    sortOrder: string = 'asc',
    columnFilters: Record<string, string> = {},
    companiesToFilter?: Array<{ company_id: string; company_name: string }> // Override for company filter
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query parameters
      const params = new URLSearchParams({
        region: region, // Add region parameter
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      // Add company_id filter - send all selected companies to backend
      // Backend will filter by company_specific_codes before pagination
      // Use override if provided (for clear filter), otherwise use state
      const companiesToUse = companiesToFilter !== undefined ? companiesToFilter : selectedCompanies;
      if (companiesToUse.length > 0) {
        const companyIds = companiesToUse.map(c => c.company_id).join(',');
        params.append('company_ids', companyIds);
      }

      if (search.trim()) {
        params.append('search', search.trim());
      }

      // Add column filters to query parameters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value.trim()) {
          params.append(`filter_${key}`, value.trim());
        }
      });

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS_V2}/?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 LOAD DEBUG - Backend response:', result);

      if (result.success) {
        // Transform suppliers data for frontend
        const transformedSuppliers = result.data.map((supplier: any) => {
          // Map categories array to category field for DataDashboard
          const categoriesArray = supplier.categories || (supplier.category ? [supplier.category] : []);

          console.log('🔍 SUPPLIER DEBUG - Raw supplier data:', {
            id: supplier._id,
            name: supplier.name,
            categories: supplier.categories,
            category: supplier.category,
            mapped: categoriesArray,
            company_specific_codes: supplier.company_specific_codes,
            recommended_by_companies: supplier.recommended_by_companies
          });

          return {
            id: supplier._id,
            global_id: supplier.supplier_id,
            name: supplier.name || '',
            // Map categories array to category field for DataDashboard (categories type expects array)
            category: categoriesArray,
            categories: categoriesArray,
            address: supplier.address || '',
            website: supplier.website || '',
            // Online capabilities
            online_sales: supplier.online_sales ? 'Yes' : 'No',
            online_marketing: supplier.online_marketing ? 'Yes' : 'No',
            // Approval status
            approval_status: supplier.approval_status || 'pending',
            // Recommended by companies - keep as array for CompanySelector
            recommended_by_companies: supplier.recommended_by_companies || [],
            // Company specific codes - for company filter feature
            company_specific_codes: supplier.company_specific_codes || [],
            // Calculate overall score from individual scores
            overall_score: calculateOverallScore(supplier),
            trustpilot_score: supplier.trustpilot_score || 0,
            google_maps_score: supplier.google_maps_score || 0,
            mebsis_score: supplier.mebsis_score || 0,
            email_score: supplier.email_score || 0,
            status: supplier.status || 'active',
            // Additional fields for reference
            _id: supplier._id,
            supplier_id: supplier.supplier_id,
            is_approved: supplier.is_approved || false,
            rejection_reason: supplier.rejection_reason || ''
          };
        });

        setSuppliers(transformedSuppliers);
        setPaginationInfo(result.pagination || {
          page: 1,
          limit: 25,
          total: 0,
          pages: 0,
          has_next: false,
          has_prev: false
        });
      } else {
        throw new Error(result.error || 'Failed to load suppliers');
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setError(error instanceof Error ? error.message : 'Failed to load suppliers');
      setSuppliers([]);
      setPaginationInfo({
        page: 1,
        limit: 25,
        total: 0,
        pages: 0,
        has_next: false,
        has_prev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate overall score from individual scores
  const calculateOverallScore = (supplier: any) => {
    const scores = [
      supplier.trustpilot_score || 0,
      supplier.google_maps_score || 0,
      supplier.mebsis_score || 0,
      supplier.email_score || 0
    ].filter(score => score > 0);

    if (scores.length === 0) return 0;
    return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1);
  };

  // Apply company filter - reload data from backend with company filter
  const handleApplyFilter = () => {
    if (selectedCompanies.length === 0) {
      setIsFiltered(false);
      setFilteredData([]);
      return;
    }

    // Mark as filtered and reload from backend (pagination will be reset to page 1)
    setIsFiltered(true);
    loadSuppliers(1, paginationInfo.limit, currentSearch, currentSortBy, currentSortOrder, currentColumnFilters);
  };

  // Clear company filter - reload data from backend without company filter
  const handleClearFilter = () => {
    setSelectedCompanies([]);
    setIsFiltered(false);
    setFilteredData([]);
    // Pass empty array to explicitly clear company filter
    loadSuppliers(1, paginationInfo.limit, currentSearch, currentSortBy, currentSortOrder, currentColumnFilters, []);
  };

  // Placeholder data fetch handler
  const handleDataFetch = (params: { page: number; limit: number; search?: string; sortBy?: string; sortOrder?: string; columnFilters?: Record<string, string> }) => {
    // Store current filter state
    setCurrentSearch(params.search || '');
    setCurrentSortBy(params.sortBy || 'global_id');
    setCurrentSortOrder((params.sortOrder as 'asc' | 'desc') || 'asc');
    setCurrentColumnFilters(params.columnFilters || {});

    loadSuppliers(params.page, params.limit || 25, params.search || '', params.sortBy || 'global_id', params.sortOrder || 'asc', params.columnFilters || {});
  };

  // Real edit handler using V2 API
  const handleEditSupplier = async (id: string, field: string, value: any) => {
    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Find the supplier - id is already ObjectId from DataDashboard
      const supplier = suppliers.find(s => s.id === id);
      if (!supplier) {
        throw new Error('Supplier not found');
      }

      // Check if editing a company code field (company_code_{companyId})
      if (field.startsWith('company_code_')) {
        const companyId = field.replace('company_code_', '');

        // Update company_specific_codes array
        let companyCodes = supplier.company_specific_codes || [];
        const existingCodeIndex = companyCodes.findIndex((c: any) => c.company_id === companyId);

        if (existingCodeIndex >= 0) {
          // Update existing code
          companyCodes[existingCodeIndex].code = value;
        } else {
          // Add new code entry
          const company = selectedCompanies.find(c => c.company_id === companyId);
          if (company) {
            companyCodes.push({
              company_id: companyId,
              company_name: company.company_name,
              code: value
            });
          }
        }

        // Send the entire company_specific_codes array to backend
        console.log(`🔧 EDIT DEBUG - Updating supplier ${id} company codes:`, companyCodes);

        const response = await fetch(`${API_ENDPOINTS.SUPPLIER_DETAILS(id)}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ company_specific_codes: companyCodes })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ EDIT ERROR - Status ${response.status}:`, errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Update local state
        const updatedSupplier = { ...supplier, company_specific_codes: companyCodes };
        setSuppliers(suppliers.map(s => s.id === id ? updatedSupplier : s));
        if (isFiltered) {
          setFilteredData(filteredData.map(s => s.id === id ? updatedSupplier : s));
        }

        return;
      }

      // Use ObjectId (_id) for PATCH request, not supplier_id
      console.log(`🔧 EDIT DEBUG - Updating supplier ${id}, field: ${field}, value:`, value);

      const response = await fetch(`${API_ENDPOINTS.SUPPLIER_DETAILS(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ EDIT ERROR - Status ${response.status}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ EDIT SUCCESS - Backend response:', result);

      if (result.success) {
        // Update local state optimistically
        const updatedSuppliers = suppliers.map(s =>
          s.id === id
            ? { ...s, [field]: value, overall_score: field.includes('_score') ? calculateOverallScore({ ...s, [field]: value }) : s.overall_score }
            : s
        );
        setSuppliers(updatedSuppliers);
        console.log('✅ EDIT SUCCESS - Local state updated');
      } else {
        throw new Error(result.error || 'Failed to update supplier');
      }
    } catch (error) {
      console.error('Error updating supplier:', error);
      alert(`Failed to update supplier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle delete click - show confirmation modal
  const handleDeleteSuppliers = (ids: string[]) => {
    setSuppliersToDelete(ids);
    setShowDeleteConfirm(true);
  };

  // Confirm delete handler using V2 API
  const handleConfirmDelete = async () => {
    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS_V2}/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          supplier_ids: suppliersToDelete,
          region: region // Include region to use correct collection
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Close confirmation modal
        setShowDeleteConfirm(false);
        setSuppliersToDelete([]);

        // Reload suppliers with current filters preserved
        await loadSuppliers(
          paginationInfo.page,
          paginationInfo.limit,
          currentSearch,
          currentSortBy,
          currentSortOrder,
          currentColumnFilters
        );

        setToastMessage(`${result.deleted_count} supplier(s) deleted successfully`);
        setToastType('success');
        setShowToast(true);
      } else {
        throw new Error(result.error || 'Failed to delete suppliers');
      }
    } catch (error) {
      console.error('Error deleting suppliers:', error);
      setShowDeleteConfirm(false);
      setToastMessage(`Failed to delete suppliers: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastType('error');
      setShowToast(true);
    }
  };

  // Real export handler
  const handleExport = async (exportData: any) => {
    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🚀 SuppliersDashboard handleExport called with:', exportData);

      // ExportModal'dan gelen data yapısını kullan
      const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🚀 SuppliersDashboard export error response:', errorText);
        throw new Error(`Export failed: ${response.status} ${errorText}`);
      }

      // Handle file download
      const blob = await response.blob();
      console.log('🚀 SuppliersDashboard export blob size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const baseFilename = exportData.filename || 'suppliers_export';
      a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('🚀 SuppliersDashboard export completed successfully');
    } catch (error) {
      console.error('🚀 SuppliersDashboard export error:', error);
      alert(`Failed to export suppliers: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Data change handler
  const handleDataChange = (newData: any[]) => {
    setSuppliers(newData);
  };

  // Load data on component mount
  useEffect(() => {
    if (token) {
      loadSuppliers();
    }
  }, [token, region]); // Re-load when region changes

  // Handle region change
  const handleRegionChange = (newRegion: Region) => {
    setRegion(newRegion);
    // Data will auto-reload via useEffect
  };

  // Custom header content with region selector and company filter
  const headerContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', position: 'relative', overflow: 'visible' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <RegionSelector value={region} onChange={handleRegionChange} />

        <div style={{ position: 'relative', zIndex: 10001, display: 'inline-block' }}>
          <CompanySelector
            value={selectedCompanies}
            onChange={setSelectedCompanies}
          />
        </div>

        {selectedCompanies.length > 0 && !isFiltered && (
          <button
            onClick={handleApplyFilter}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Apply Filter
          </button>
        )}

        {isFiltered && (
          <button
            onClick={handleClearFilter}
            style={{
              padding: '5px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            Clear Filter
          </button>
        )}

        <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          Viewing suppliers from: <strong>{region.toUpperCase()}</strong>
        </span>
      </div>

      {/* Selected Companies Display */}
      {selectedCompanies.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #e9ecef'
        }}>
          {selectedCompanies.map((company) => (
            <span
              key={company.company_id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                backgroundColor: '#e3f2fd',
                color: '#1565c0',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500'
              }}
            >
              {company.company_name}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCompanies(selectedCompanies.filter(c => c.company_id !== company.company_id));
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1565c0',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '14px',
                  lineHeight: '1',
                  marginLeft: '2px'
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-v2-dashboard-container">
      <DataDashboard
        title="Suppliers"
        collection="suppliers"
        headerContent={headerContent}
        data={suppliers}
        columns={supplierColumns}
        isLoading={isLoading}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={handleDataChange}
        onEdit={handleEditSupplier}
        onEditToggle={handleEditToggle}
        isEditing={isEditing}
        onDelete={handleDeleteSuppliers}
        onExport={handleExport}
        onDataFetch={handleDataFetch}
        paginationInfo={paginationInfo}
        showAddButton={true}
        showSearch={true}
        showSelection={true}
        showEditButton={true}
        showDeleteButton={true}
        onAdd={handleAddSupplier}
      />

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
        onSuccess={handleSupplierAdded}
        user={user}
        currentRegion={region}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSuppliersToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Suppliers"
        message={`Are you sure you want to delete ${suppliersToDelete.length} supplier(s)? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default SuppliersDashboard;
